import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiX, FiMusic, FiPlay, FiPause, FiLayers, FiType, FiLoader } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { PlateTemplatePicker, PlateTemplateBadge, usePlateCatalogue } from './PlateTemplatePicker';

// The package look on the Scenes step: the breaking plates' design and
// kicker, the music settings, an overlay frame per scene (with a live preview
// over the scene's picture), and the music under each part.

/** The plates' design and kicker, and the music settings. */
export const LookPanel = ({ story, options, run, readOnly, t }) => {
  const id = story._id;
  const pkg = story.package || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [choosingPlate, setChoosingPlate] = useState(false);
  const [choosingTemplate, setChoosingTemplate] = useState(false);
  const plateStyles = options?.plateStyles || [];
  const plateUsed = plateStyles.find((s) => s.id === story.plateStyleUsed);
  const toggle = () => {
    // The form starts from the story's kicker each time it opens.
    if (!open) setForm({ kicker: pkg.kicker || '' });
    setOpen(!open);
  };
  const save = async () => {
    const next = await run(() => api.update(id, { packageText: { kicker: form.kicker } }));
    if (next) setOpen(false);
  };
  const volume = story.options?.musicVolume ?? 0.3;

  return (
    <section className="card space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><FiLayers size={14} /> {t('fullStoryVideo.look.title')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('fullStoryVideo.look.hint')}</p>
        </div>
        {!readOnly && (
          <button className="btn btn-ghost text-xs py-1.5 flex items-center gap-1" onClick={toggle}>
            <FiType size={12} /> {t('fullStoryVideo.look.editText')}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
        <label className="flex items-center gap-2 text-gray-700">
          <input type="checkbox" checked={story.options?.autoMusic !== false} disabled={readOnly}
            onChange={(e) => run(() => api.updateOptions(id, { autoMusic: e.target.checked }))} />
          {t('fullStoryVideo.look.autoMusic')}
        </label>
        <label className="flex items-center gap-2 text-gray-700">
          <FiMusic size={13} /> {t('fullStoryVideo.look.musicVolume')}
          <input type="range" min={0.05} max={0.8} step={0.05} defaultValue={volume} disabled={readOnly} className="w-28"
            onMouseUp={(e) => run(() => api.updateOptions(id, { musicVolume: Number(e.currentTarget.value) }))}
            onTouchEnd={(e) => run(() => api.updateOptions(id, { musicVolume: Number(e.currentTarget.value) }))}
            onKeyUp={(e) => run(() => api.updateOptions(id, { musicVolume: Number(e.currentTarget.value) }))} />
          <span className="tabular-nums text-xs text-gray-500">{Math.round(volume * 100)}%</span>
        </label>
      </div>
      {story.plateTemplatesOn && story.plateTemplateUsed && (
        <PlateTemplateRow story={story} readOnly={readOnly} onChange={() => setChoosingTemplate(true)} t={t} />
      )}
      {choosingTemplate && (
        <PlateTemplatePicker
          title={t('fullStoryVideo.look.plateTemplate')}
          hint={t('fullStoryVideo.look.plateTemplateHint')}
          current={story.plateTemplateUsed}
          onPick={async (templateId) => { const next = await run(() => api.update(id, { packageText: { plateTemplate: templateId } })); if (next) setChoosingTemplate(false); }}
          onClose={() => setChoosingTemplate(false)}
        />
      )}
      {!story.plateTemplatesOn && plateUsed && (
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
          <img src={api.platePreviewUrl(plateUsed.id, story.aspectRatio)} alt="" className={`rounded border border-gray-200 ${story.aspectRatio === '9:16' ? 'h-24' : 'h-16'}`} />
          <div className="text-sm">
            <p className="text-gray-800">{t('fullStoryVideo.look.plateDesign')}: <strong>{plateUsed.name}</strong></p>
            <p className="text-xs text-gray-500">{pkg.plateStyle ? t('fullStoryVideo.look.plateChosen') : t('fullStoryVideo.look.plateAuto')}</p>
          </div>
          {!readOnly && <button className="btn btn-ghost text-xs py-1.5" onClick={() => setChoosingPlate(true)}>{t('fullStoryVideo.look.plateChange')}</button>}
        </div>
      )}
      {choosingPlate && (
        <PlatePicker story={story} styles={plateStyles} run={run} onClose={() => setChoosingPlate(false)} t={t} />
      )}
      {open && form && (
        <div className="flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
          <label className="text-xs text-gray-600 space-y-1">{t('fullStoryVideo.look.kicker')}
            <input value={form.kicker} onChange={(e) => setForm({ kicker: e.target.value })} maxLength={40} className="input w-56 text-sm" placeholder="చాలావుంది!" />
          </label>
          <div className="flex items-center gap-2 text-sm">
            <button className="btn btn-ghost text-xs py-1.5" onClick={() => setOpen(false)}>{t('fullStoryVideo.look.cancel')}</button>
            <button className="btn btn-primary text-xs py-1.5" onClick={save}>{t('fullStory.saveEdits')}</button>
          </div>
        </div>
      )}
    </section>
  );
};

/** The story's graphic plate template: its poster, who chose it, and Change. */
const PlateTemplateRow = ({ story, readOnly, onChange, t }) => {
  const [catalogue] = usePlateCatalogue();
  const by = story.package?.plateTemplateBy;
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
      <PlateTemplateBadge templateId={story.plateTemplateUsed} catalogue={catalogue} className="text-sm text-gray-800 font-medium" />
      <p className="text-xs text-gray-500">
        {t('fullStoryVideo.look.plateTemplate')} · {by === 'user' ? t('fullStoryVideo.look.plateTemplateYou') : by === 'ai' ? t('fullStoryVideo.look.plateTemplateAi') : t('fullStoryVideo.look.plateTemplateDefault')}
      </p>
      {!readOnly && <button className="btn btn-ghost text-xs py-1.5" onClick={onChange}>{t('fullStoryVideo.look.plateChange')}</button>}
    </div>
  );
};

/** Choose the plates' design — one for the whole video — from previews of every design. */
const PlatePicker = ({ story, styles, run, onClose, t }) => {
  const vertical = story.aspectRatio === '9:16';
  const choose = async (styleId) => {
    const next = await run(() => api.update(story._id, { packageText: { plateStyle: styleId } }));
    if (next) onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-6xl max-h-[94vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">{t('fullStoryVideo.look.plateDesign')}</h3>
            <p className="text-xs text-gray-500">{t('fullStoryVideo.look.plateHint')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:bg-gray-100" aria-label={t('prompts.close')}><FiX size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <ul className={`grid gap-3 ${vertical ? 'grid-cols-3 sm:grid-cols-5 lg:grid-cols-7' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`}>
            {styles.map((s) => (
              <li key={s.id}>
                <button onClick={() => choose(s.id)}
                  className={`w-full text-left rounded-lg border p-1.5 ${story.plateStyleUsed === s.id ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200 hover:border-primary-300'}`}>
                  <img src={api.platePreviewUrl(s.id, story.aspectRatio)} alt="" loading="lazy"
                    className={`w-full rounded bg-gray-100 ${vertical ? 'aspect-[9/16]' : 'aspect-video'} object-cover`} />
                  <span className="block text-xs text-gray-700 mt-1 truncate">{s.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        {story.package?.plateStyle && (
          <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
            <button className="btn btn-ghost text-sm" onClick={() => choose('')}>{t('fullStoryVideo.look.plateAutoUse')}</button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * The music under a scene: a track, your uploaded track or none — for this
 * scene or its whole part — with a button to listen first.
 */
export const MusicSelect = ({ story, scene, options, run, readOnly, t }) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const tracks = options?.musicTracks || [];
  const value = scene.music || '';
  const ranged = scene.kind === 'spot' || scene.kind === 'byte';
  useEffect(() => () => audioRef.current?.pause(), []);
  if (ranged) return <span className="text-[11px] text-gray-400">{t('fullStoryVideo.music.ownSound')}</span>;

  const listen = () => {
    if (!value || value === 'custom') return;
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    if (!audioRef.current) audioRef.current = new Audio();
    audioRef.current.src = api.musicUrl(value);
    audioRef.current.onended = () => setPlaying(false);
    audioRef.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  const change = (e) => {
    audioRef.current?.pause();
    setPlaying(false);
    run(() => api.updateScene(story._id, scene._id, { music: e.target.value || 'none', musicForPart: true }));
  };

  return (
    <span className="inline-flex items-center gap-1">
      <FiMusic size={12} className={value ? 'text-primary-600' : 'text-gray-400'} />
      <select value={value} onChange={change} disabled={readOnly} className="text-xs border border-gray-200 rounded-md py-0.5 pl-1.5 pr-6 bg-white max-w-[11rem]"
        title={t('fullStoryVideo.music.forPart')}>
        <option value="">{t('fullStoryVideo.music.none')}</option>
        {tracks.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        {story.options?.music?.filename && <option value="custom">{t('fullStoryVideo.music.custom')}</option>}
      </select>
      {value && value !== 'custom' && (
        <button className="p-1 rounded text-gray-500 hover:bg-gray-100" onClick={listen} title={t('fullStoryVideo.music.listen')} aria-label={t('fullStoryVideo.music.listen')}>
          {playing ? <FiPause size={12} /> : <FiPlay size={12} />}
        </button>
      )}
    </span>
  );
};

/**
 * Choose an overlay frame for a scene: by category, with the frame playing
 * over the scene's own picture (blended the way the video blends it) and an
 * opacity to set.
 */
export const FramePicker = ({ story, scene, options, thumb, run, onClose, t }) => {
  const frames = options?.frames || [];
  const categories = options?.frameCategories || {};
  const [category, setCategory] = useState(() => frames.find((f) => f.id === scene.frame?.id)?.category || Object.keys(categories)[0]);
  const [selected, setSelected] = useState(scene.frame?.id || null);
  const [opacity, setOpacity] = useState(scene.frame?.opacity ?? frames.find((f) => f.id === scene.frame?.id)?.opacity ?? 0.8);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const frame = frames.find((f) => f.id === selected);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return frames.filter((f) => (q ? `${f.label} ${f.hint}`.toLowerCase().includes(q) : f.category === category));
  }, [frames, category, query]);
  const vertical = story.aspectRatio === '9:16';

  const pick = (f) => {
    setSelected(f.id);
    setOpacity(f.opacity ?? 0.8);
    setLoading(true);
  };
  const apply = async (frameId) => {
    const next = await run(() => api.updateScene(story._id, scene._id, { frame: frameId ? { id: frameId, opacity } : 'none' }));
    if (next) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-5xl max-h-[94vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{t('fullStoryVideo.frame.title')}</h3>
          <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:bg-gray-100" aria-label={t('prompts.close')}><FiX size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 space-y-3">
            <div className={`relative mx-auto rounded-lg overflow-hidden bg-gray-900 ${vertical ? 'aspect-[9/16] max-h-[60vh]' : 'aspect-video w-full'}`}>
              {thumb && <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              {frame && (
                <video key={`${frame.id}-${story.aspectRatio}`} src={api.frameUrl(frame.id, story.aspectRatio)} autoPlay loop muted playsInline
                  onCanPlay={() => setLoading(false)} onError={() => setLoading(false)}
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                  style={{ mixBlendMode: frame.mode === 'multiply' ? 'multiply' : 'screen', opacity }} />
              )}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-xs gap-2">
                  <FiLoader className="animate-spin" /> {t('fullStoryVideo.frame.preparing')}
                </div>
              )}
            </div>
            {frame && (
              <div className="space-y-2">
                <p className="text-sm text-gray-800"><strong>{frame.label}</strong> <span className="text-gray-500">— {frame.hint}</span></p>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  {t('fullStoryVideo.frame.opacity')}
                  <input type="range" min={0.1} max={1} step={0.05} value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} className="flex-1" />
                  <span className="tabular-nums w-10 text-right">{Math.round(opacity * 100)}%</span>
                </label>
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {scene.frame?.id && <button className="btn btn-ghost text-sm text-danger-600" onClick={() => apply(null)}>{t('fullStoryVideo.frame.remove')}</button>}
              <button className="btn btn-primary text-sm" disabled={!frame} onClick={() => apply(frame.id)}>{t('fullStoryVideo.frame.use')}</button>
            </div>
          </div>
          <div className="lg:col-span-2 space-y-2">
            <input value={query} onChange={(e) => setQuery(e.target.value)} className="input w-full text-sm" placeholder={t('fullStoryVideo.frame.search')} />
            {!query.trim() && (
              <div className="flex flex-wrap gap-1">
                {Object.entries(categories).map(([key, label]) => (
                  <button key={key} onClick={() => setCategory(key)}
                    className={`text-xs rounded-full px-2.5 py-1 border ${category === key ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-200 text-gray-600 hover:border-primary-300'}`}>
                    {t(`fullStoryVideo.frame.category.${key}`) === `fullStoryVideo.frame.category.${key}` ? label : t(`fullStoryVideo.frame.category.${key}`)}
                  </button>
                ))}
              </div>
            )}
            <ul className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
              {shown.map((f) => (
                <li key={f.id}>
                  <button onClick={() => pick(f)}
                    className={`w-full text-left rounded-lg border px-2.5 py-1.5 ${selected === f.id ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-primary-300'}`}>
                    <span className="text-sm text-gray-800">{f.label}</span>
                    <span className="block text-[11px] text-gray-500 truncate">{f.hint}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
